import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, Upload, Loader2, Check, X, Edit2, 
  Package, Tag, DollarSign, Barcode, Scale,
  Calendar, Box, Sparkles, RotateCcw, Save,
  AlertCircle, CheckCircle2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIProductOnboard() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [step, setStep] = useState('capture'); // capture, processing, review, saving
  const [imageData, setImageData] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Extracted product data
  const [productData, setProductData] = useState({
    name: '',
    brand: '',
    category: '',
    mrp: '',
    selling_price: '',
    weight: '',
    ingredients: '',
    expiry_date: '',
    barcode: '',
    packaging_type: '',
    description: '',
    stock: '',
    sku: '',
    gst_rate: '0',
    available_in_pos: true,
    push_to_online: false
  });

  const categories = [
    'Millets', 'Sweets & Snacks', 'Oils', 'Dry Fruits', 
    'Spices', 'Rice & Grains', 'Beverages', 'Pickles',
    'Health Foods', 'Bakery', 'Dairy', 'Others'
  ];

  const packagingTypes = ['Packet', 'Bottle', 'Box', 'Jar', 'Tin', 'Pouch', 'Bag'];
  const token = localStorage.getItem('pos_admin_token');

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (error) {
      toast.error('Camera access denied. Please use upload instead.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setCameraActive(false);
    }
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setImageData(dataUrl);
      
      // Convert to file for upload
      canvas.toBlob((blob) => {
        const file = new File([blob], 'product-photo.jpg', { type: 'image/jpeg' });
        setImageFile(file);
      }, 'image/jpeg', 0.8);
      
      stopCamera();
      processImage(dataUrl);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageData(e.target.result);
        processImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Process image with AI
  const processImage = async (base64Image) => {
    setStep('processing');
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/pos/ai/extract-product`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image: base64Image })
      });

      if (!response.ok) {
        throw new Error('AI extraction failed');
      }

      const data = await response.json();
      
      // Update product data with AI extracted info
      setProductData(prev => ({
        ...prev,
        name: data.name || '',
        brand: data.brand || '',
        category: data.category || '',
        mrp: data.mrp || '',
        selling_price: data.mrp || '', // Default selling price to MRP
        weight: data.weight || '',
        ingredients: data.ingredients || '',
        expiry_date: data.expiry_date || '',
        barcode: data.barcode || '',
        packaging_type: data.packaging_type || '',
        description: data.description || ''
      }));

      setStep('review');
      toast.success('Product details extracted! Please review and edit.');
    } catch (error) {
      toast.error('Failed to extract product details. Please enter manually.');
      setStep('review');
    } finally {
      setProcessing(false);
    }
  };

  // Save product
  const handleSave = async () => {
    // Validation
    if (!productData.name) {
      toast.error('Product name is required');
      return;
    }
    if (!productData.selling_price) {
      toast.error('Selling price is required');
      return;
    }
    if (!productData.stock) {
      toast.error('Stock quantity is required');
      return;
    }

    setSaving(true);
    setStep('saving');

    try {
      // First upload image if exists
      let imageUrl = null;
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        
        const uploadResponse = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageUrl = uploadData.url;
        }
      }

      // Create product
      const productPayload = {
        name: productData.name,
        brand: productData.brand,
        category_id: productData.category,
        price: parseFloat(productData.mrp) || parseFloat(productData.selling_price),
        sale_price: parseFloat(productData.selling_price),
        inventory: parseInt(productData.stock) || 0,
        sku: productData.sku || `SKU-${Date.now()}`,
        weight: productData.weight,
        description: productData.description || `${productData.name} - ${productData.brand}`,
        gst_rate: parseFloat(productData.gst_rate) || 0,
        images: imageUrl ? [imageUrl] : [],
        barcode: productData.barcode,
        expiry_date: productData.expiry_date,
        packaging_type: productData.packaging_type,
        ingredients: productData.ingredients,
        available_in_pos: productData.available_in_pos,
        push_to_online: productData.push_to_online,
        ai_generated: true,
        created_via: 'ai_scan'
      };

      const response = await fetch(`${API_URL}/api/pos/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productPayload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save product');
      }

      const result = await response.json();
      toast.success('Product saved successfully!');
      navigate('/pos/admin/products');
    } catch (error) {
      toast.error(error.message);
      setStep('review');
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setStep('capture');
    setImageData(null);
    setImageFile(null);
    setProductData({
      name: '', brand: '', category: '', mrp: '', selling_price: '',
      weight: '', ingredients: '', expiry_date: '', barcode: '',
      packaging_type: '', description: '', stock: '', sku: '', gst_rate: '0',
      available_in_pos: true, push_to_online: false
    });
    stopCamera();
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="ai-product-onboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-500" />
          AI Product Scanner
        </h1>
        <p className="text-gray-500">Capture or upload a product image to auto-fill details</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        {['Capture', 'Processing', 'Review', 'Save'].map((label, idx) => {
          const stepMap = { 'Capture': 'capture', 'Processing': 'processing', 'Review': 'review', 'Save': 'saving' };
          const isActive = step === stepMap[label];
          const isPast = ['capture', 'processing', 'review', 'saving'].indexOf(step) > idx;
          
          return (
            <React.Fragment key={label}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                isActive ? 'border-amber-500 bg-amber-500 text-white' :
                isPast ? 'border-emerald-500 bg-emerald-500 text-white' :
                'border-gray-300 text-gray-400'
              }`}>
                {isPast ? <Check className="w-5 h-5" /> : idx + 1}
              </div>
              {idx < 3 && (
                <div className={`w-20 h-1 ${isPast ? 'bg-emerald-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step: Capture */}
      {step === 'capture' && (
        <div className="bg-white rounded-2xl border shadow-sm p-8">
          <div className="text-center mb-8">
            <Camera className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Capture Product Image</h2>
            <p className="text-gray-500 mt-2">Take a photo or upload an image of the product label</p>
          </div>

          {cameraActive ? (
            <div className="relative max-w-md mx-auto">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                className="w-full rounded-xl"
              />
              <canvas ref={canvasRef} className="hidden" />
              <div className="flex gap-3 mt-4 justify-center">
                <Button onClick={capturePhoto} className="bg-amber-500 hover:bg-amber-600">
                  <Camera className="w-4 h-4 mr-2" />
                  Capture Photo
                </Button>
                <Button variant="outline" onClick={stopCamera}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
              <Button 
                onClick={startCamera}
                className="h-32 flex-col gap-2 bg-slate-800 hover:bg-slate-900"
              >
                <Camera className="w-8 h-8" />
                <span>Open Camera</span>
              </Button>
              
              <Button 
                variant="outline"
                className="h-32 flex-col gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-8 h-8" />
                <span>Upload Image</span>
              </Button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          )}

          <div className="mt-8 p-4 bg-blue-50 rounded-xl">
            <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Tips for best results
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Ensure good lighting on the product label</li>
              <li>• Keep the camera steady and close to the label</li>
              <li>• Include MRP, weight, and brand in the frame</li>
              <li>• Barcode should be visible if present</li>
            </ul>
          </div>
        </div>
      )}

      {/* Step: Processing */}
      {step === 'processing' && (
        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center">
          <div className="max-w-md mx-auto">
            {imageData && (
              <img src={imageData} alt="Product" className="w-full rounded-xl mb-6" />
            )}
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Analyzing Product...</h2>
            <p className="text-gray-500 mt-2">AI is extracting product details from the image</p>
          </div>
        </div>
      )}

      {/* Step: Review */}
      {step === 'review' && (
        <div className="bg-white rounded-2xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-amber-500" />
              Review & Edit Details
            </h2>
            <Button variant="ghost" onClick={resetForm}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Image Preview */}
            <div className="lg:col-span-1">
              {imageData && (
                <div className="sticky top-6">
                  <img src={imageData} alt="Product" className="w-full rounded-xl border" />
                  <p className="text-xs text-center text-gray-500 mt-2">Captured Image</p>
                </div>
              )}
            </div>

            {/* Form Fields */}
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name *
                  </label>
                  <Input
                    value={productData.name}
                    onChange={(e) => setProductData({...productData, name: e.target.value})}
                    placeholder="Enter product name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <Input
                    value={productData.brand}
                    onChange={(e) => setProductData({...productData, brand: e.target.value})}
                    placeholder="Brand name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={productData.category}
                    onChange={(e) => setProductData({...productData, category: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border bg-white"
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Packaging</label>
                  <select
                    value={productData.packaging_type}
                    onChange={(e) => setProductData({...productData, packaging_type: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border bg-white"
                  >
                    <option value="">Select type</option>
                    {packagingTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MRP (₹)</label>
                  <Input
                    type="number"
                    value={productData.mrp}
                    onChange={(e) => setProductData({...productData, mrp: e.target.value})}
                    placeholder="Maximum retail price"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price (₹) *</label>
                  <Input
                    type="number"
                    value={productData.selling_price}
                    onChange={(e) => setProductData({...productData, selling_price: e.target.value})}
                    placeholder="Your selling price"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                  <Input
                    value={productData.weight}
                    onChange={(e) => setProductData({...productData, weight: e.target.value})}
                    placeholder="e.g., 500g, 1kg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity *</label>
                  <Input
                    type="number"
                    value={productData.stock}
                    onChange={(e) => setProductData({...productData, stock: e.target.value})}
                    placeholder="Available stock"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU Code</label>
                  <Input
                    value={productData.sku}
                    onChange={(e) => setProductData({...productData, sku: e.target.value})}
                    placeholder="Auto-generated if empty"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                  <Input
                    value={productData.barcode}
                    onChange={(e) => setProductData({...productData, barcode: e.target.value})}
                    placeholder="Product barcode"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GST Rate (%)</label>
                  <select
                    value={productData.gst_rate}
                    onChange={(e) => setProductData({...productData, gst_rate: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border bg-white"
                  >
                    <option value="0">0% (Exempt)</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                  <Input
                    value={productData.expiry_date}
                    onChange={(e) => setProductData({...productData, expiry_date: e.target.value})}
                    placeholder="e.g., Dec 2025"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ingredients</label>
                <textarea
                  value={productData.ingredients}
                  onChange={(e) => setProductData({...productData, ingredients: e.target.value})}
                  placeholder="Product ingredients..."
                  className="w-full h-20 px-3 py-2 rounded-lg border resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={productData.description}
                  onChange={(e) => setProductData({...productData, description: e.target.value})}
                  placeholder="Product description..."
                  className="w-full h-20 px-3 py-2 rounded-lg border resize-none"
                />
              </div>

              {/* Availability Toggles */}
              <div className="flex flex-wrap gap-6 pt-4 border-t">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productData.available_in_pos}
                    onChange={(e) => setProductData({...productData, available_in_pos: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="font-medium">Available in POS</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productData.push_to_online}
                    onChange={(e) => setProductData({...productData, push_to_online: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="font-medium">Push to Online Store</span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-2" />
                      Save Product
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={resetForm} className="h-12">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step: Saving */}
      {step === 'saving' && (
        <div className="bg-white rounded-2xl border shadow-sm p-8 text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Saving Product...</h2>
          <p className="text-gray-500 mt-2">Please wait while we save the product</p>
        </div>
      )}
    </div>
  );
}
